import { addDays } from "date-fns";
import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { useTranslation } from "react-i18next";
import { UseQueryResult } from "react-query";
import { MemoType } from "../../common/enums/MemoType";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { IObjectHistoryAlarmItem, IObjectHistoryDataQualityEventItem, IObjectHistoryDiagnosticEventItem, IObjectHistoryFormEventItem, IObjectHistoryItem, IObjectHistoryMemoItem, IObjectHistorySettingChangeItem, IObjectHistoryWaterLossEventItem, ObjectHistoryItemTypeEnum, getObjectHistoryItemTypeName } from "../../queries/object-history/Common";
import { useDeleteHistoryItem } from "../../queries/object-history/UseDeleteHistoryItem";
import { useHistoryItems } from "../../queries/object-history/UseHistoryItems";
import { useObjectProperties } from "../../queries/object-properties/UseObjectProperties";
import { useObjectViewState } from "../../queries/object-view-state/UseObjectViewState";
import { IObjectPropertiesDeviceModel, IObjectPropertiesTankBatteryModel, IObjectPropertiesTankModel, IObjectPropertiesWellModel } from "../../services/properties-data-service/Models";
import storageService from "../../services/storage/storage-service/StorageService";
import { BaseActionEnum } from "../action-menu/ActionMenuItem";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import { CustomDurationDialog, CustomDurationDialogValues, bindCustomDurationDialog, useCustomDurationDialogState } from "../custom-duration-dialog/CustomDurationDialog";
import DiagnosticEventEditDialog, { bindDiagnosticEventEditDialog, useDiagnosticEventEditDialogState } from "../diagnostic-event-edit-dialog/DiagnosticEventEditDialog";
import MemoEditDialog, { bindMemoEditDialog, useMemoEditDialogState } from "../memo-edit-dialog/MemoEditDialog";
import NoData from "../no-data/NoData";
import VirtualizedList, { VirtualizedListItem } from "../virtualized-list/VirtualizedList";
import WaterLossEventEditDialog, { bindWaterLossEventEditDialog, useWaterLossEventEditDialogState } from "../water-loss-event-edit-dialog/WaterLossEventEditDialog";
import { ObjectHistoryAlarmItem } from "./ObjectHistoryAlarmItem";
import { OBJECT_HISTORY_VIEW_OPTION_STORAGE_KEY, useObjectHistoryContext } from "./ObjectHistoryContext";
import { ObjectHistoryDataQualityEventItem } from "./ObjectHistoryDataQualityEventItem";
import { ObjectHistoryDiagnosticEventItem } from "./ObjectHistoryDiagnosticEventItem";
import { ObjectHistoryFormEventItem } from "./ObjectHistoryFormEventItem";
import { ObjectHistoryMemoItem, ObjectHistoryMemoItemActionEnum } from "./ObjectHistoryMemoItem";
import { ObjectHistorySettingChangeItem } from "./ObjectHistorySettingChangeItem";
import { ObjectHistorySpacerItem } from "./ObjectHistorySpacerItem";
import { ObjectHistoryWaterLossEventItem } from "./ObjectHistoryWaterLossEventItem";
import { ProgressStateItem, useProgressContext } from "../../contexts/progress-context/ProgressContext";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import UUID from "../../common/utilities/Uuid";
import { ActionTypeEnum, StatusEnum } from "../../common/IExportNotificationMsg";
import { ApplicationMimeTypesEnum } from "../../common/enums/ApplicationMimeTypesEnum";
import { ISortParameters } from "../../common/models/ISortParameters";
import TagDataService from "../../services/tag-data-service/TagDataService";
import { RestError } from "../../services/rest-service/RestService";
import { DurationOptionEnum } from "../custom-duration-dialog/CustomDurationUtils";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import ObjectHistoryDataService from "../../services/history-data-service/HistoryDataService";
import { EventDurationEnum, IObjectHistoryExportOptions } from "../../services/history-data-service/Models";
import { NavigationSource } from "../../common/enums/NavigationSource";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import FileDownloader, { FileDownloaderType } from "../file-downloader/FileDownloader";
import FileViewerDialog, { bindFileViewerDialog, useFileViewerDialogState } from "../file-viewer-dialog/FileViewerDialog";
import { AttachmentListCardActionEnum } from "../attachment-list/AttachmentListCard";
import { IAttachment } from "../../common/models/IAttachment";
import { getAttachmentUrl } from "../../common/utilities/AttachmentHelpers";

export type ObjectHistoryItem = IObjectHistoryItem & VirtualizedListItem;

export type ObjectHistoryProps = {
    visible?: boolean;
    groupID: number;
    contextObject: IeLynxObject;
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
    dense?: boolean;
};

export type ObjectHistoryType = {
    executeAction: (action: BaseActionEnum | BaseActionEnum) => void;
};

function ObjectHistory({
    visible,
    groupID,
    contextObject,
    setViewOptionState,
    dense
}: ObjectHistoryProps, ref: ForwardedRef<ObjectHistoryType> | undefined) {
    const [userState] = useUserContext();
    const { t } = useTranslation();
    const memoDialog = useMemoEditDialogState();
    const eventDialog = useDiagnosticEventEditDialogState();
    const waterLossDialog = useWaterLossEventEditDialogState();
    const [offlineState] = useOfflineDetectContext();
    const { confirm, acknowledge } = useConfirmation();
    const durationDialog = useCustomDurationDialogState();
    const [progressState, actionStarted] = useProgressContext();   
    const [, setAppNotificationState] = useAppNotificationContext(); 
    const routeContext = useRouteContext();
    
    const fileViewerDialog = useFileViewerDialogState();
    const fileDownloaderRef = useRef<FileDownloaderType>(null);


    const {
        state,
        setDuration,
        setScrollTo,
        setDownloadSettingsUid,
        setDownloadAlarmsUid,
        setHasAlarmHistory,
        setHasSettingsHistory,
        resetOptions
    } = useObjectHistoryContext();

    useImperativeHandle<ObjectHistoryType, ObjectHistoryType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    const { data: objectViewState } = useQueryNotifications(useObjectViewState)({
        customerID: userState.customer?.id as number,
        groupID: groupID,
        contextObject: {
            id: contextObject.id,
            objectType: contextObject?.objectType
        },
        offline: offlineState.offline
    });

    const objectName = objectViewState?.contextObject.objectName ?? "";

    const { data, status, refetch } = useQueryNotifications(useHistoryItems)({
        customerID: userState.customer?.id as number,
        groupID: groupID,
        eLynxObject: {
            id: contextObject.id,
            objectType: contextObject.objectType
        },
        offline: offlineState.offline,
        duration: state.durationOptions.option,
        startDate: state.durationOptions.duration.startDate,
        endDate: state.durationOptions.duration.endDate,
        options: {
            showMemos: state.showMemos,
            showDataQualityEvents: state.showDataQualityEvents,
            showDiagnosticEvents: state.showDiagnosticEvents,
            showAlarms: state.showAlarms,
            showSettingsChanges: state.showSettingsChanges,
            showFormEvents: state.showFormEvents,
            showManualEvents: state.showManualEvents,
            filterText: state.filterText,
        },
        enabled: visible
    });

    const { mutate: deleteHistoryItem/*, status: deleteStatus*/ } = useQueryNotifications(useDeleteHistoryItem)({
        customerID: userState.customer?.id as number,
        groupID: groupID,
        eLynxObject: {
            id: contextObject.id,
            objectType: contextObject.objectType
        },
        offline: offlineState.offline,
        onSuccess: (_data, variables) => {
            const itemName = getObjectHistoryItemTypeName(variables);
            acknowledge(t("deleteSuccessMessage", { itemName: itemName }));
        }
    });

    const { data: properties } = useQueryNotifications(useObjectProperties)({
        groupID: groupID,
        eLynxObject: contextObject,
    }) as UseQueryResult<IObjectPropertiesDeviceModel |
        IObjectPropertiesTankModel |
        IObjectPropertiesTankBatteryModel |
        IObjectPropertiesWellModel | undefined>;

    const canModifyMemos = properties?.canModifyMemos ?? false;

    useEffect(() => {
        if (visible) {
            const viewOptionState = state.isOptionsDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            setViewOptionState && setViewOptionState(viewOptionState);
        }
    }, [
        visible,
        state.isOptionsDirty,
        setViewOptionState
    ]);

    async function createMemo() {
        const newMemo: IObjectHistoryMemoItem = {
            type: ObjectHistoryItemTypeEnum.Memo,
            memoID: -1, //offlineState.offline ? await OfflineDataService.getNewMemoID(userState.customer?.id as number) : newMemoIndex.current--,
            timestamp: new Date(),
            text: "",
            memoType: MemoType.Memo,
            userName: userState.user?.loginName ?? "",
            userID: userState.user?.id ?? 0,
            objectType: contextObject.objectType,
            objectID: contextObject.id,
            attachments: []
        };

        await memoDialog.open(newMemo);
    }

    async function editMemo(memo: IObjectHistoryMemoItem) {
        await memoDialog.open(memo);
    }

    async function confirmAndDeleteMemo(memo: IObjectHistoryMemoItem) {

        const canDeleteMemo = await getDeleteMemoConfirmation();

        if (canDeleteMemo) {
            deleteHistoryItem(memo);
        }
    }

    async function onMemoAction(action: ObjectHistoryMemoItemActionEnum, item: IObjectHistoryMemoItem) {
        if (canModifyMemos) {
            switch (action) {
                case ObjectHistoryMemoItemActionEnum.Edit:
                    editMemo(item);
                    break;
                case ObjectHistoryMemoItemActionEnum.Delete:
                    confirmAndDeleteMemo(item);
                    break;
            }
        }
    }

    async function getDeleteMemoConfirmation() {

        return (await confirm({
            dialogTitle: t("deleteConfirmation.title", { itemName: t("memo") }),
            confirmationText: t("deleteConfirmation.confirmationText"),
            yesButtonText: t("deleteConfirmation.yesButtonText"),
            noButtonText: t("deleteConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    async function createDiagnosticEvent() {
        const now = new Date();
        const newEvent = {
            startTime: addDays(now, -1),
            endTime: now,
            ruleName: "Testing",
            note: ""
        } as IObjectHistoryDiagnosticEventItem;

        await eventDialog.open(newEvent);
    }

    async function createWaterLossEvent() {
        const now = new Date();
        const newEvent = {

            startTime: now,
            endTime: now,

            estimatedLoss: undefined,
            estimatedLossUOM: 37,
            cause: undefined,
            notes: undefined

        } as IObjectHistoryWaterLossEventItem;

        await waterLossDialog.open(newEvent);
    }

    async function onCustomDuration() {
        const result = await durationDialog.open(state.durationOptions);

        if (result.reason === "ok") {
            const values = result.values as CustomDurationDialogValues;

            setDuration(values);
        }
    }

    const _getProgressState = (uid: string, title: string): ProgressStateItem => {
        return {
            uid: uid,
            objectID: contextObject.id,
            objectType: contextObject.objectType,
            title: title, 
            notificationMsg: {
                action: ActionTypeEnum.Download,
                notificationID: uid,
                status: StatusEnum.Started,
                statusMessage: t('progress.sendingRequest'),
                additionalInfo: '',
                blobURI: '',
                percentComplete: 0
            }
        };
    }

    function onRefreshData() {

        if (state.durationOptions.option !== DurationOptionEnum.Custom) {

            let dayOffset = -1;

            switch (state.durationOptions.option) {
                //case DurationOptionEnum.OneDay:
                //    break;

                case DurationOptionEnum.ThreeDays:
                    dayOffset = -3;
                    break;

                case DurationOptionEnum.SevenDays:
                    dayOffset = -7;
                    break;

                case DurationOptionEnum.ThirtyDays:
                    dayOffset = -30;
                    break;

                default:
                    break;
            }

            const endDate = new Date();
            const startDate = addDays(endDate, dayOffset);

            setDuration({...state.durationOptions, 
                duration: {
                    startDate: startDate,
                    endDate: endDate
            }});

         }

         refetch();
    }


    async function onExportTagData(action: BaseActionEnum | BaseActionEnum) {

        const sortParams: ISortParameters = {
            propertyName: 'timestamp',
            sortAscending: false
        };

        if (state.showSettingsChanges && data?.history.some(o => o.type === ObjectHistoryItemTypeEnum.SettingChange)) {

            const uid = UUID.generate();
            setDownloadSettingsUid(uid);
    
            actionStarted(_getProgressState(uid, 'Tag Control Settings'));
    
            const mimeType = action === BaseActionEnum.Excel ? 
                                    ApplicationMimeTypesEnum.MSExcel2007 : 
                                    ApplicationMimeTypesEnum.PDF;
            try {
   
                await TagDataService.exportControlHistory(uid, 
                    groupID!,
                    contextObject.id,
                    mimeType, 
                    sortParams,
                    state.durationOptions.option === DurationOptionEnum.Default ? 
                                    undefined : 
                                    state.durationOptions.duration.startDate,
                    state.durationOptions.option === DurationOptionEnum.Default ? 
                                    undefined : 
                                    state.durationOptions.duration.endDate,
                );

    
            } catch(error) {
                const restError = error as RestError;
                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Error,
                    show: true,
                    error: restError
                });
            }
        }

        if (state.showAlarms && data?.history.some(o => o.type === ObjectHistoryItemTypeEnum.Alarm)) {
            const uid = UUID.generate();
            setDownloadAlarmsUid(uid);
    
            actionStarted(_getProgressState(uid, 'Tag Alarm History'));
    
            const mimeType = action === BaseActionEnum.Excel ? 
                                    ApplicationMimeTypesEnum.MSExcel2007 : 
                                    ApplicationMimeTypesEnum.PDF;
            try {
   
                await TagDataService.exportAlarmHistory(uid, 
                    groupID!,
                    contextObject.id,
                    mimeType, 
                    sortParams,
                    state.durationOptions.option === DurationOptionEnum.Default ? 
                                    undefined : 
                                    state.durationOptions.duration.startDate,
                    state.durationOptions.option === DurationOptionEnum.Default ? 
                                    undefined : 
                                    state.durationOptions.duration.endDate,
                );
    
    
            } catch(error) {
                const restError = error as RestError;
                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Error,
                    show: true,
                    error: restError
                });
            }

        }

    }

    async function onExportDeviceData(action: BaseActionEnum | BaseActionEnum) {

        const sortParams: ISortParameters = {
            propertyName: 'timestamp',
            sortAscending: false
        };

        const uid = UUID.generate();
        setDownloadSettingsUid(uid);

        actionStarted(_getProgressState(uid, `${getObjectTypeName(contextObject.objectType)} History`));

        const mimeType = action === BaseActionEnum.Excel ? 
                                ApplicationMimeTypesEnum.MSExcel2007 : 
                                ApplicationMimeTypesEnum.PDF;
        try {

            const options: IObjectHistoryExportOptions = {
                notificationKey: uid,
                mimeType: mimeType,
                duration: state.durationOptions.option === DurationOptionEnum.OneDay
                                ? EventDurationEnum.Last24Hours
                                : state.durationOptions.option === DurationOptionEnum.ThreeDays
                                    ? EventDurationEnum.Last3Days
                                    : state.durationOptions.option === DurationOptionEnum.SevenDays
                                        ? EventDurationEnum.Last7Days
                                        : state.durationOptions.option === DurationOptionEnum.ThirtyDays
                                            ? EventDurationEnum.Last30Days
                                            : state.durationOptions.option === DurationOptionEnum.Custom
                                                ? EventDurationEnum.Custom
                                                : EventDurationEnum.Recent100,
                beginDate: state.durationOptions.option === DurationOptionEnum.Custom ? 
                                state.durationOptions.duration.startDate : 
                                undefined,
                endDate: state.durationOptions.option === DurationOptionEnum.Custom ? 
                                state.durationOptions.duration.endDate : 
                                undefined,
                sortParameters: sortParams,
                includeMemos: state.showMemos,
                includeAlarmEvents: state.showAlarms,
                includeControlEvents: state.showSettingsChanges,
                filterText: state.filterText
            }
            await ObjectHistoryDataService.exportHistory(
                groupID!,
                contextObject,
                options
            );


        } catch(error) {
            const restError = error as RestError;
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                show: true,
                error: restError
            });
        }
    }

    function executeAction(action: BaseActionEnum | BaseActionEnum) {

        switch (action) {
            case BaseActionEnum.Refresh:
                onRefreshData();
                break;

            case BaseActionEnum.AddMemo:
                createMemo();
                break;

            case BaseActionEnum.AddDiagnositicEvent:
                createDiagnosticEvent();
                break;

            case BaseActionEnum.AddWaterLossEvent:
                createWaterLossEvent();
                break;

            case BaseActionEnum.ShowDurationSettings:
                onCustomDuration();
                break;

            case BaseActionEnum.Excel:
            case BaseActionEnum.PDF:
                if (contextObject.objectType === eLynxObjectTypeEnum.Tag) {
                    onExportTagData(action);
                } else if (contextObject.objectType === eLynxObjectTypeEnum.Device || 
                    contextObject.objectType === eLynxObjectTypeEnum.Tank) {
                        onExportDeviceData(action);
                }
                
                break;
    
        }
    }

        async function onAttachmentAction(action: AttachmentListCardActionEnum, payload: IAttachment) {
    
            switch(action) {
    
                case AttachmentListCardActionEnum.View: {
                    const attachment = payload as IAttachment;
    
                    await fileViewerDialog.open({
                        title: attachment.description,
                        url: getUrl(attachment.base64Data, attachment.url),
                        mimeType: attachment.mimeType
                    });
                    // console.log(`viewing ${attachment.fileName}`);
                }
                break;
    
                case AttachmentListCardActionEnum.Download: {
                    const attachment = payload as IAttachment;
                    fileDownloaderRef.current?.download(
                        getAttachmentUrl(attachment.base64Data, attachment.url),
                        attachment.fileName);
                }
                break;
            }
    
        }
    
    

    function renderItem(_index: number, item: IObjectHistoryItem) {

        return (
            <>
                {
                    item?.type === ObjectHistoryItemTypeEnum.Alarm
                        ? <ObjectHistoryAlarmItem
                            item={item as IObjectHistoryAlarmItem}
                            contextObject={contextObject}
                        />
                        : item?.type === ObjectHistoryItemTypeEnum.Memo
                            ? <ObjectHistoryMemoItem
                                item={item as IObjectHistoryMemoItem}
                                contextObject={contextObject}
                                onAction={onMemoAction}
                                onAttachmentAction={onAttachmentAction}
                                readOnly={!canModifyMemos}
                            />
                            : item?.type === ObjectHistoryItemTypeEnum.SettingChange
                                ? <ObjectHistorySettingChangeItem
                                    item={item as IObjectHistorySettingChangeItem}
                                    contextObject={contextObject}
                                />
                                : item?.type === ObjectHistoryItemTypeEnum.DataQualityEvent
                                    ? <ObjectHistoryDataQualityEventItem
                                        item={item as IObjectHistoryDataQualityEventItem}
                                        contextObject={contextObject}
                                    />
                                    : item?.type === ObjectHistoryItemTypeEnum.DiagnosticEvent
                                        ? <ObjectHistoryDiagnosticEventItem
                                            item={item as IObjectHistoryDiagnosticEventItem}
                                            contextObject={contextObject}
                                        />
                                        : item?.type === ObjectHistoryItemTypeEnum.FormEvent
                                            ? <ObjectHistoryFormEventItem
                                                item={item as IObjectHistoryFormEventItem}
                                                contextObject={contextObject}
                                            />
                                            : item?.type === ObjectHistoryItemTypeEnum.WaterLossEvent
                                                ? <ObjectHistoryWaterLossEventItem
                                                    item={item as IObjectHistoryWaterLossEventItem}
                                                    contextObject={contextObject}
                                                />
                                                : <ObjectHistorySpacerItem />
                }
            </>);
    }


    useEffect(() => {
        if (data) {

            const memoID = storageService.session.get("memoID", -1);
            if (memoID !== -1) {

                storageService.session.remove('memoID');
                const itemIndex = data?.history.findIndex(o => o.type === ObjectHistoryItemTypeEnum.Memo && (o as IObjectHistoryMemoItem)?.memoID === memoID);

                if (itemIndex && state.scrollTo === undefined) {
                    setScrollTo(itemIndex);
                }
            } else if (state.scrollTo) {
                setScrollTo(undefined);
            }
        }

    }, [
        data,
        state.scrollTo,
        setScrollTo,
    ]);


    const progress = progressState.items.filter(o => o.objectID === contextObject.id && o.objectType === contextObject.objectType); 

    useEffect(() => {

        const settingsProgress = progress.find(o => o.uid === state.downloadSettingsUid);
        if (settingsProgress?.notificationMsg.status === StatusEnum.Completed ||
            settingsProgress?.notificationMsg.status === StatusEnum.Failed) {
            setDownloadSettingsUid(undefined);
        }



        const alarmsProgress = progress.find(o => o.uid === state.downloadAlarmsUid);
        if (alarmsProgress?.notificationMsg.status === StatusEnum.Completed ||
            alarmsProgress?.notificationMsg.status === StatusEnum.Failed) {
            setDownloadAlarmsUid(undefined);
        }
    }, [
        groupID,
        progress,
        state.downloadSettingsUid,
        state.downloadAlarmsUid,
        setDownloadSettingsUid,
        setDownloadAlarmsUid
    ]);

    useEffect(() => {

        if (data?.history.some(o => o.type === ObjectHistoryItemTypeEnum.Alarm) && state.showAlarms) {
            if (!state.hasAlarmHistory) {
                setHasAlarmHistory(true);
            }
        } else {
            if (state.hasAlarmHistory) {
                setHasAlarmHistory(false);
            }
        }

        if (data?.history.some(o => o.type === ObjectHistoryItemTypeEnum.SettingChange && state.showSettingsChanges)) {
            if (!state.hasSettingsHistory) {
                setHasSettingsHistory(true);
            }
        } else {
            if (state.hasSettingsHistory) {
                setHasSettingsHistory(false);
            }
        }


    }, [
        data,
        state.showAlarms,
        state.showSettingsChanges,
        state.hasAlarmHistory,
        state.hasSettingsHistory,
        setHasAlarmHistory,
        setHasSettingsHistory
    ]);


    const noDataMessage = data?.totalCount === 0
        ? t("noData.noDataTypesForObject", { dataTypeName: t("dataTypeNames.historyItem"), objectName })
        : data?.filteredCount === 0
            ? t("noData.noFilteredDataTypesForObject", { dataTypeName: t("dataTypeNames.historyItem"), objectName })
            : undefined;


    useEffect(() => {
        if (routeContext.navSrc !== NavigationSource.Breadcrumb && 
            routeContext.navSrc !== NavigationSource.ArrowPrev &&
            routeContext.navSrc !== NavigationSource.ArrowNext) {
            storageService.local.remove(OBJECT_HISTORY_VIEW_OPTION_STORAGE_KEY);

            resetOptions();
        }
    }, [
        routeContext.navSrc,
        resetOptions
    ]);
        

    return (
        <>
            {visible &&
                <>
                    {status === "success" && noDataMessage &&
                        <NoData text={noDataMessage} />
                    }

                    <VirtualizedList
                        items={data?.history as Array<ObjectHistoryItem>}
                        renderItem={renderItem}
                        scrollTo={state.scrollTo}
                    />

                    {
                    !dense && canModifyMemos &&
                        <>
                            <MemoEditDialog groupID={groupID} contextObject={contextObject} {...bindMemoEditDialog(memoDialog)} />
                            <DiagnosticEventEditDialog groupID={groupID} contextObject={contextObject} {...bindDiagnosticEventEditDialog(eventDialog)} />
                            <WaterLossEventEditDialog groupID={groupID} contextObject={contextObject} {...bindWaterLossEventEditDialog(waterLossDialog)} />
                        </>
                    }

                    {
                    !dense && <CustomDurationDialog
                        overrideDefaultText={t('customDuration.last100')}
                        value={state.durationOptions}
                        {...bindCustomDurationDialog(durationDialog)}
                    />
                     }

                    <FileDownloader ref={fileDownloaderRef} />
                    <FileViewerDialog {...bindFileViewerDialog(fileViewerDialog)} />

                </>
            }
        </>
    );
}

export default forwardRef(ObjectHistory);

function getUrl(base64Data: string | undefined, url: string | undefined): string | undefined {
    throw new Error("Function not implemented.");
}
